import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { CircleUser, Settings } from 'lucide-react';
import { SettingsModal } from './SettingsModal';
import { useBrandSettings } from '../hooks/useBrandSettings';
import { cn } from '../lib/utils';

export default function AppHeader() {
  const [user, setUser] = useState<any>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { settings: brandSettings, saveSettings } = useBrandSettings();

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const res = await fetch('/api/auth/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        setUser(data.user);
      } catch {}
    })();
  }, [location.pathname]);

  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  async function onLogout() {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      navigate('/');
    }
  }

  // Brand dashboard tabs
  const brandTabs = [
    { id: 'overview', label: 'Dashboard Overview', path: `/brand/${user?.tenantId}/dashboard` },
    { id: 'operational', label: 'Operational Performance', path: `/brand/${user?.tenantId}/dashboard/operational` },
    { id: 'stock', label: 'Stock Reporting', path: `/brand/${user?.tenantId}/dashboard/stock` },
    { id: 'sales', label: 'Sales Performance', path: `/brand/${user?.tenantId}/dashboard/sales` },
    { id: 'usage', label: 'Usage Analytics', path: `/brand/${user?.tenantId}/dashboard/usage` },
    { id: 'media', label: 'Retail Media Performance', path: `/brand/${user?.tenantId}/dashboard/media` },
  ];

  // Custom function to determine if a tab is active
  const isTabActive = (tabPath: string) => {
    if (!user?.tenantId) return false;
    // For the overview tab, only match exactly
    if (tabPath === `/brand/${user.tenantId}/dashboard`) {
      return location.pathname === tabPath;
    }
    // For other tabs, check if current path starts with the tab path
    return location.pathname.startsWith(tabPath);
  };

  return (
    <>
      <header className={cn(
        "sticky top-0 z-40 w-full border-b bg-background transition-all duration-300",
        isScrolled ? "shadow-sm" : "shadow-none"
      )}>
        <div className={cn(
          "container mx-auto flex items-center justify-between px-6 transition-all duration-300",
          isScrolled ? "h-16" : "h-20"
        )}>
          {/* Left side - Logo/Brand */}
          <div className="flex items-center">
            {user?.role !== 'brand' && (
              <Link to="/home" className={cn(
                "font-semibold text-blue-600 transition-all duration-300",
                isScrolled ? "text-xl" : "text-2xl"
              )}>Bright Blue Cloud</Link>
            )}
            {user?.role === 'brand' && brandSettings.logo && (
              <img 
                src={brandSettings.logo} 
                alt="Brand Logo" 
                className={cn(
                  "object-contain transition-all duration-300",
                  isScrolled ? "h-10" : "h-14"
                )}
              />
            )}
            {user?.role === 'admin' && (
              <nav className="flex items-center gap-6 ml-12">
                <Button asChild variant="ghost" size="sm" className="text-gray-700 hover:text-gray-900">
                  <Link to="/home">Home</Link>
                </Button>
                <Button asChild variant="ghost" size="sm" className="text-gray-700 hover:text-gray-900">
                  <Link to="/admin/users">Users</Link>
                </Button>
                <Button asChild variant="ghost" size="sm" className="text-gray-700 hover:text-gray-900">
                  <Link to="/data/upload">Data Upload</Link>
                </Button>
                <Button asChild variant="ghost" size="sm" className="text-gray-700 hover:text-gray-900">
                  <Link to="/analytics">Analytics</Link>
                </Button>
              </nav>
            )}
          </div>

          {/* Center - Brand Navigation Menu */}
          {user?.role === 'brand' && (
            <nav className="flex items-center gap-8 ml-8" aria-label="Dashboard sections">
              {brandTabs.map((tab) => {
                const isActive = isTabActive(tab.path);
                return (
                  <Link
                    key={tab.id}
                    to={tab.path}
                    className={cn(
                      'font-medium text-sm transition-colors duration-200 relative',
                      isActive
                        ? 'font-semibold'
                        : 'text-gray-600 hover:text-gray-900'
                    )}
                    style={isActive ? {
                      color: brandSettings.primaryColor
                    } : undefined}
                  >
                    {tab.label}
                    {isActive && (
                      <div 
                        className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full"
                        style={{ backgroundColor: brandSettings.primaryColor }}
                      />
                    )}
                  </Link>
                );
              })}
            </nav>
          )}

          {/* Right side - User info and actions */}
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg">
                  <CircleUser className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700 hidden sm:inline">{user.email}</span>
                </div>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  title="Settings"
                  onClick={() => setIsSettingsOpen(true)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <Settings className="w-5 h-5" />
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={onLogout}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Logout
                </Button>
              </>
            ) : (
              <Button asChild size="sm">
                <Link to="/">Login</Link>
              </Button>
            )}
          </div>
        </div>
      </header>
      
      {/* Settings Modal */}
      {user?.role === 'brand' && (
        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          onSave={saveSettings}
          currentSettings={brandSettings}
        />
      )}
    </>
  );
}
